'use client';
import React from 'react';
import { Container, Section } from '@layout';
import {Button, Card, Chip, Heading} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';

interface HeroDemoProps {
  className?: string;
  sectionID?: string;
  classes?: string;
  heroClass?: string; // Added to pass hero-specific classes if needed
  isSpanish?: boolean;
}

const HeroDemo: React.FC<HeroDemoProps> = ({
  isSpanish = false,
  className = '',
  sectionID = '',
  classes = '',
  heroClass = '',
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  return (
    <div {...withOptionalId(sectionID)}>
      <Section>
        <Container>
          <div className='page-hero-desc'>
            <Heading as="h3">
              {className === 'reverse'
                ? t('Page Hero Reverse', 'Hero de pagina invertido')
                : t('Page Hero', 'Hero de pagina')}
            </Heading>
            <p className='sub full'>
              {className === 'reverse'
                ? t(
                    'This is the same page hero, but in reverse order, including gradients reversed.',
                    'Este es el mismo hero de pagina, pero en orden invertido, incluyendo gradientes invertidos.'
                  )
                : t(
                    'A hero component that showcases key content and actions, with a visually striking background and prominent call-to-action buttons.',
                    'Componente hero que muestra contenido y acciones clave, con fondo visualmente llamativo y botones principales de llamada a la accion.'
                  )}
            </p>
          </div>
        </Container>
      </Section>
      <Section className={heroClass}>
        <Container className='hero-inner'>
          <div className='hero-grid'>
            <div className='hero-main'>
              <span className='eyebrow row btn-row'>
                <Chip label={t('Regulatory-ready', 'Listo para regulacion')} />
                <Chip label={t('Collaboration-first', 'Colaboracion primero')} />
              </span>
              <div className='h1'>Tulio Solorzano</div>
              <p className='sub full'>
                {t(
                  'Web Development Manager and Web Developer building production web applications in React and TypeScript, with shared component systems and the teams that ship them.',
                  'Web Development Manager y Web Developer que construye aplicaciones web de producción en React y TypeScript, con sistemas de componentes compartidos y los equipos que las entregan.'
                )}
              </p>
              <div className='row btn-row'>
                <Button
                  as='a'
                  className='btn-secondary'
                  href='#solutions'
                  label={t('See solutions', 'Ver soluciones')}
                />
                <Button
                  as='a'
                  className='btn-ghost'
                  href='#platform'
                  label={t('Explore platform →', 'Explorar plataforma →')}
                />
              </div>
              <div className='kpis'>
                <Card className='kpi'>
                  <b>98.7%</b>
                  <small>{t('on-time deliverables', 'entregas a tiempo')}</small>
                </Card>
                <Card className='kpi'>
                  <b>40%↓</b>
                  <small>{t('handoff friction', 'friccion en handoff')}</small>
                </Card>
                <Card className='kpi'>
                  <b>21 CFR</b>
                  <small>{t('Part 11 aligned', 'alineado a Parte 11')}</small>
                </Card>
              </div>
            </div>
            <div className='hero-card'>
              <div className='chips row btn-row mb-16'>
                <Chip
                  className='secondary'
                  label={t('Live Study View', 'Vista de estudio en vivo')}
                />
                <Chip className='tertiary' label={t('Milestone Alerts', 'Alertas de hitos')} />
              </div>
              <div
                className='mock'
                role='img'
                aria-label={t('Platform preview card', 'Tarjeta de vista previa de plataforma')}
              ></div>
              <div className='row btn-row mt-16'>
                <Button className='btn-primary' label={t('Request demo', 'Solicitar demo')} />
                <Button
                  as='a'
                  className='btn-ghost'
                  href='#process'
                  label={t('How it works', 'Como funciona')}
                />
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default HeroDemo;
